import { useCallback, useContext } from "react";
import AppContext from "../context/AppContext";
import { readStoredAdminEmail } from "../utils/adminAuthUtils";

/**
 * Optional per-entry user whitelist declared on a sidebar option in
 * `src/utils/Constants.js`. When present, only admins whose email or username
 * is listed may see the entry. See
 * `docs/components/sider/sidebar-entries-visibility.md`.
 */
export type UserWhitelist =
  | {
      emails?: string | string[] | null;
      usernames?: string | string[] | null;
    }
  | null
  | undefined;

type AdminIdentity = {
  adminEmail?: string | null;
  adminUsername?: string | null;
};

const normalizeIdentity = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeWhitelistValues = (
  values: string | string[] | null | undefined,
): string[] => {
  if (values == null) return [];
  const list = Array.isArray(values) ? values : [values];
  return list.map(normalizeIdentity).filter(Boolean);
};

/**
 * Pure predicate so non-React code can reuse the same check.
 *
 * Restrictive only: a missing/empty `userWhitelist` means "no restriction"
 * (everyone passes). When a whitelist is present, the admin must match by
 * email OR username. Matching does NOT grant any other access — callers still
 * apply permission, development-mode, and feature-flag checks separately.
 */
export function isUserWhitelisted(
  userWhitelist: UserWhitelist,
  { adminEmail, adminUsername }: AdminIdentity,
): boolean {
  if (userWhitelist == null) return true;

  const whitelistedEmails = normalizeWhitelistValues(userWhitelist.emails);
  const whitelistedUsernames = normalizeWhitelistValues(
    userWhitelist.usernames,
  );
  const currentEmail = normalizeIdentity(adminEmail);
  const currentUsername = normalizeIdentity(adminUsername);

  return (
    (currentEmail.length > 0 && whitelistedEmails.includes(currentEmail)) ||
    (currentUsername.length > 0 &&
      whitelistedUsernames.includes(currentUsername))
  );
}

// Local view of the `AppContext` shape — only the identity fields this hook
// needs. `AppContext` lives in a .js file created without a generic, so we
// narrow it here instead of widening the whole context.
type AppContextShape = AdminIdentity;

/**
 * Reactive hook bound to the current admin's identity. Returns a predicate
 * that evaluates a sidebar entry's `user_whitelist` against the logged-in
 * admin (`adminEmail` / `adminUsername` from AppContext, with email falling
 * back to the login email stored in localStorage).
 *
 * @example
 *   const isWhitelisted = useUserWhitelist();
 *   if (!isWhitelisted(option.user_whitelist)) return null;
 */
export function useUserWhitelist(): (userWhitelist: UserWhitelist) => boolean {
  const { adminEmail, adminUsername } = useContext(
    AppContext,
  ) as AppContextShape;
  const resolvedAdminEmail = adminEmail ?? readStoredAdminEmail();

  return useCallback(
    (userWhitelist: UserWhitelist) =>
      isUserWhitelisted(userWhitelist, {
        adminEmail: resolvedAdminEmail,
        adminUsername,
      }),
    [resolvedAdminEmail, adminUsername],
  );
}

export default useUserWhitelist;
